import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ onFinish, logoUrl }) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setIsExiting(true);
            setTimeout(onFinish, 800);
          }, 400);
          return 100;
        }
        return prev + 3;
      });
    }, 50);
    return () => clearInterval(timer);
  }, [onFinish]);

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.15 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 30%, #2563eb 60%, #1e40af 100%)' }}
        >
          {/* Animated background orbs */}
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.08, 0.2, 0.08],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute w-[500px] h-[500px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(61, 168, 158, 0.2) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.05, 0.12, 0.05],
            }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute w-[400px] h-[400px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(77, 184, 168, 0.15) 0%, transparent 70%)',
              filter: 'blur(60px)',
              top: '20%',
              left: '30%',
            }}
          />

          {/* Rotating ring */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            className="absolute w-48 h-48 rounded-full"
            style={{
              border: '1px solid rgba(61, 168, 158, 0.25)',
              borderTop: '2px solid rgba(61, 168, 158, 0.7)',
            }}
          />

          {/* Reverse rotating ring */}
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
            className="absolute w-56 h-56 rounded-full"
            style={{
              border: '1px solid rgba(61, 168, 158, 0.15)',
              borderBottom: '1px solid rgba(61, 168, 158, 0.4)',
            }}
          />

          {/* Logo */}
          <motion.div
            initial={{ scale: 0, rotate: -180, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ duration: 1, type: 'spring', stiffness: 80, damping: 12 }}
            className="relative z-10"
          >
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img
                src={logoUrl}
                alt="ג'יני מובייל"
                className="w-36 h-36 rounded-full object-cover"
                style={{
                  boxShadow: '0 0 50px rgba(96, 165, 250, 0.25), 0 0 100px rgba(96, 165, 250, 0.1)',
                  border: '2px solid rgba(96, 165, 250, 0.35)',
                }}
              />
            </motion.div>
          </motion.div>

          {/* Title with reveal */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
            className="text-3xl font-black mt-6 z-10 text-white"
            style={{ fontFamily: 'Heebo, sans-serif' }}
          >
            ג'יני מובייל
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="text-lg mt-1 z-10 gold-text-shimmer font-semibold"
          >
            Genie Mobile
          </motion.p>

          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: '12rem' }}
            transition={{ delay: 1, duration: 0.5 }}
            className="mt-8 h-1 rounded-full overflow-hidden z-10"
            style={{ background: 'rgba(61, 168, 158, 0.2)' }}
          >
            <motion.div
              className="h-full rounded-full"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #93c5fd, #6ee7b7, #5cc9bd)',
                boxShadow: '0 0 10px rgba(61, 168, 158, 0.7)',
                transition: 'width 0.08s ease',
              }}
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ delay: 1.2 }}
            className="mt-3 text-xs text-blue-200 z-10 font-medium"
          >
            טוען...
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
